import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Header.css';

interface HeaderProps {
  onGenerateReport?: () => void;
  canGenerateReport?: boolean;
  isGeneratingReport?: boolean;
  onNewScreening?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  onGenerateReport,
  canGenerateReport = false,
  isGeneratingReport = false,
  onNewScreening,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    closeMenu();
    setUserDropdownOpen(false);
    await logout();
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  return (
    <header className="header">
      {isAdmin && user?.stripe_test_mode && (
        <div className="stripe-test-banner">
          STRIPE TEST MODE
        </div>
      )}
      <div className="header-content">
        <Link to="/" className="header-logo" onClick={closeMenu}>
          <img src="/HeaderLogo.png" alt="CompanySieve logo" className="header-logo-img" />
          <div className="header-logo-text">
            <h1>CompanySieve</h1>
            <span className="header-subtitle">Export Compliance Screening</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav-desktop">
          {isAuthenticated ? (
            <>
              <Link
                to="/"
                className={`nav-link ${isActive('/') ? 'active' : ''}`}
                onClick={() => onNewScreening?.()}
              >
                New Screening
              </Link>
              <Link
                to="/history"
                className={`nav-link ${isActive('/history') ? 'active' : ''}`}
              >
                History
              </Link>
              {isAdmin && (
                <>
                  <Link
                    to="/accuracy"
                    className={`nav-link ${isActive('/accuracy') ? 'active' : ''}`}
                  >
                    Accuracy
                  </Link>
                  <Link
                    to="/admin/users"
                    className={`nav-link ${isActive('/admin/users') ? 'active' : ''}`}
                  >
                    Users
                  </Link>
                </>
              )}
              {canGenerateReport && (
                <button
                  onClick={onGenerateReport}
                  disabled={isGeneratingReport}
                  className="header-pdf-button"
                >
                  {isGeneratingReport ? 'Generating...' : 'Download PDF'}
                </button>
              )}
              <div className="user-dropdown-container" ref={dropdownRef}>
                <button
                  className="user-dropdown-trigger"
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                >
                  {user?.name || user?.email} <span className="dropdown-caret">&#9662;</span>
                </button>
                {userDropdownOpen && (
                  <div className="user-dropdown">
                    <Link
                      to="/account"
                      className="dropdown-item"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      Account Settings
                    </Link>
                    <button
                      className="dropdown-item dropdown-logout"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className={`nav-link ${isActive('/login') ? 'active' : ''}`}>
                Sign In
              </Link>
              <Link to="/register" className={`nav-link ${isActive('/register') ? 'active' : ''}`}>
                Sign Up
              </Link>
            </>
          )}
        </nav>

        {/* Mobile: PDF button + Hamburger */}
        <div className="header-mobile-actions">
          {canGenerateReport && isAuthenticated && (
            <button
              onClick={onGenerateReport}
              disabled={isGeneratingReport}
              className="header-pdf-button mobile"
            >
              {isGeneratingReport ? '...' : 'PDF'}
            </button>
          )}
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </div>

      {/* Mobile Slide-out Menu */}
      <div className={`mobile-menu-overlay ${menuOpen ? 'open' : ''}`} onClick={closeMenu}></div>
      <nav className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <span>Menu</span>
          <button className="mobile-menu-close" onClick={closeMenu} aria-label="Close menu">
            &times;
          </button>
        </div>
        {isAuthenticated ? (
          <>
            <div className="mobile-user-info">
              {user?.name || user?.email}
            </div>
            <Link
              to="/"
              className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
              onClick={() => { closeMenu(); onNewScreening?.(); }}
            >
              New Screening
            </Link>
            <Link
              to="/history"
              className={`mobile-nav-link ${isActive('/history') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              History
            </Link>
            {isAdmin && (
              <>
                <Link
                  to="/accuracy"
                  className={`mobile-nav-link ${isActive('/accuracy') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Accuracy
                </Link>
                <Link
                  to="/admin/users"
                  className={`mobile-nav-link ${isActive('/admin/users') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Users
                </Link>
              </>
            )}
            <Link
              to="/account"
              className={'mobile-nav-link ' + (location.pathname === '/account' ? 'active' : '')}
              onClick={closeMenu}
            >
              Account Settings
            </Link>
            <button className="mobile-nav-link logout" onClick={handleLogout}>
              Logout
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              className={`mobile-nav-link ${isActive('/login') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className={`mobile-nav-link ${isActive('/register') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              Sign Up
            </Link>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
